// Fetches the daily generated stories from the backend on app boot. Returns
// null while loading or on error — call sites fall back to the static
// catalogue in @/data/stories until a real response arrives.

import { useEffect, useState } from 'react';
import type { Persona, Story } from '@/lib/types';
import { getAuth } from '@/lib/auth/auth';
import { STORIES_ENABLED } from '@/data/stories';

interface StoriesResponse {
  date: string;
  stories: Record<string, Story[]>;
  fresh: boolean;
}

export interface DailyStories {
  /** Server-side story-batch date (UTC YYYY-MM-DD). Used to key "viewed" state
   *  so the Instagram-style ring color resets when fresh stories arrive. */
  date: string;
  stories: Record<string, Story[]>;
}

const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? '/api';

interface PersonaForStory {
  id: string;
  name: string;
  en: string;
  occupation: string;
  cityEn: string;
  systemPrompt: string;
  topics: { hz: string; py: string; en: string }[];
  accent?: string;
}

function toServerShape(p: Persona): PersonaForStory {
  return {
    id: p.id,
    name: p.name,
    en: p.en,
    occupation: p.occupation,
    cityEn: p.cityEn,
    systemPrompt: p.systemPrompt,
    topics: p.topics.map((t) => ({ hz: t.hz, py: t.py, en: t.en })),
    accent: p.accent,
  };
}

export function useDailyStories(personas: readonly Persona[]): DailyStories | null {
  const [data, setData] = useState<DailyStories | null>(null);

  useEffect(() => {
    // Feature kill-switch: skip the fetch entirely so no API cost is incurred
    // and no network request appears in devtools while the feature is off.
    if (!STORIES_ENABLED) return;
    let cancelled = false;
    const ctrl = new AbortController();
    (async () => {
      try {
        const headers: Record<string, string> = { 'content-type': 'application/json' };
        const jwt = getAuth().current()?.jwt;
        if (jwt) headers.authorization = `Bearer ${jwt}`;
        const res = await fetch(`${API_BASE}/stories`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ personas: personas.map(toServerShape) }),
          signal: ctrl.signal,
          credentials: 'include',
        });
        if (!res.ok) return;
        const body = (await res.json()) as StoriesResponse;
        if (!cancelled) setData({ date: body.date, stories: body.stories });
      } catch {
        // Silent — call sites fall back to static.
      }
    })();
    return () => {
      cancelled = true;
      ctrl.abort();
    };
  }, [personas]);

  return data;
}
